import React, { useRef, useState, useEffect } from 'react';
import { Button, Space, Input, Mentions, Typography, Upload, Dropdown, message as antMessage } from 'antd';
import { CommentOutlined, PaperClipOutlined, CloseCircleFilled, BulbOutlined, DownOutlined } from '@ant-design/icons';
import api from '../api';

const MAX_SIZE = 5 * 1024 * 1024;

export default function CommentBox({
  onSubmit,
  loading,
  placeholder,
  // The button's words, where "Add Comment" alone is unclear about where the
  // comment goes (e.g. "Add Project Comment").
  submitLabel = 'Add Comment',
  // @mentions: [{ id, name }] who may be mentioned. When given, typing @
  // offers them, and onSubmit gets a third argument: the ids of those still
  // @named in the text when it is posted.
  mentionPeople,
  disabled,
  // controlled text (optional — CasePage needs to read it externally)
  value,
  onChange,
  // Optional ready-made texts to drop into the comment ([{ key, label, text }],
  // e.g. a Help Desk Request Type's solutions). Picking one adds its text to
  // what is there, to be edited before sending.
  insertOptions,
  insertLabel = 'Insert',
}) {
  const [internalText, setInternalText] = useState('');
  const [stagedFiles, setStagedFiles] = useState([]);
  const isControlled = value !== undefined;
  const text = isControlled ? value : internalText;
  const setText = isControlled ? onChange : setInternalText;

  const stageFile = (file) => {
    if (file.size > MAX_SIZE) {
      antMessage.error(`${file.name} exceeds 5 MB limit`);
      return;
    }
    const previewUrl = file.type.startsWith('image/') ? URL.createObjectURL(file) : null;
    setStagedFiles((prev) => [
      ...prev,
      { uid: `${Date.now()}-${Math.random()}`, name: file.name, type: file.type, file, previewUrl },
    ]);
  };

  const removeFile = (uid) => {
    setStagedFiles((prev) => {
      const f = prev.find((x) => x.uid === uid);
      if (f?.previewUrl) URL.revokeObjectURL(f.previewUrl);
      return prev.filter((x) => x.uid !== uid);
    });
  };

  const handlePaste = (e) => {
    const items = Array.from(e.clipboardData?.items || []);
    for (const item of items) {
      if (item.kind === 'file' && item.type.startsWith('image/')) {
        const f = item.getAsFile();
        if (f) stageFile(f);
      }
    }
  };

  // An attachment can go on its own: with no text, the comment names the
  // files, since a comment needs some text.
  const canSubmit = !!text.trim() || stagedFiles.length > 0;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    const trimmed = text.trim() || `Attached: ${stagedFiles.map((f) => f.name).join(', ')}`;
    const files = stagedFiles;
    // clear optimistically
    setText('');
    setStagedFiles([]);
    files.forEach((f) => { if (f.previewUrl) URL.revokeObjectURL(f.previewUrl); });
    const mentioned = (mentionPeople || []).filter((p) => trimmed.includes(`@${p.name}`)).map((p) => p.id);
    await onSubmit(trimmed, files, mentioned);
  };

  return (
    <Space direction="vertical" style={{ width: '100%' }} size={6}>
      <div onPaste={handlePaste}>
        {mentionPeople ? (
          <Mentions
            autoSize={{ minRows: 2, maxRows: 10 }}
            value={text}
            onChange={setText}
            placeholder={placeholder || 'Add a comment…'}
            disabled={disabled}
            maxLength={4000}
            // Names have spaces: the whole name is inserted, so only the
            // characters typed after @ narrow the list.
            split=" "
            filterOption={(input, opt) => (opt.value || '').toLowerCase().includes(input.toLowerCase())}
            options={mentionPeople.map((p) => ({ key: String(p.id), value: p.name, label: p.name }))}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) handleSubmit();
            }}
          />
        ) : (
          <Input.TextArea
            autoSize={{ minRows: 2, maxRows: 10 }}
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={placeholder || 'Add a comment…'}
            disabled={disabled}
            maxLength={4000}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) handleSubmit();
            }}
          />
        )}
      </div>

      {stagedFiles.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {stagedFiles.map((f) => (
            <div key={f.uid} style={{ position: 'relative', display: 'inline-block' }}>
              {f.previewUrl ? (
                <img
                  src={f.previewUrl}
                  alt={f.name}
                  style={{ height: 64, maxWidth: 120, objectFit: 'cover', borderRadius: 4, border: '1px solid var(--ant-color-border)' }}
                />
              ) : (
                <div style={{
                  padding: '8px 12px', border: '1px solid var(--ant-color-border)',
                  borderRadius: 4, fontSize: 12, maxWidth: 140,
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>
                  {f.name}
                </div>
              )}
              <CloseCircleFilled
                onClick={() => removeFile(f.uid)}
                style={{
                  position: 'absolute', top: -6, right: -6,
                  color: 'var(--ant-color-error)', cursor: 'pointer',
                  fontSize: 14, background: 'var(--ant-color-bg-container)', borderRadius: '50%',
                }}
              />
            </div>
          ))}
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
        <Upload
          multiple
          showUploadList={false}
          disabled={disabled}
          beforeUpload={(file) => { stageFile(file); return false; }}
          accept="image/*,.pdf,.docx,.xlsx,.csv"
        >
          <Button size="small" icon={<PaperClipOutlined />} disabled={disabled}>
            Attach
          </Button>
        </Upload>
        {insertOptions?.length > 0 && (
          <Dropdown
            disabled={disabled}
            trigger={['click']}
            menu={{
              items: insertOptions.map((o) => ({ key: String(o.key), label: o.label })),
              onClick: ({ key }) => {
                const o = insertOptions.find((x) => String(x.key) === key);
                if (!o) return;
                setText(text.trim() ? `${text.replace(/\s+$/, '')}\n\n${o.text}` : o.text);
              },
            }}
          >
            <Button size="small" icon={<BulbOutlined />} disabled={disabled} style={{ marginLeft: 8, marginRight: 'auto' }}>
              {insertLabel} <DownOutlined />
            </Button>
          </Dropdown>
        )}
        <Space size={12} align="center" wrap style={{ marginLeft: 'auto', justifyContent: 'flex-end' }}>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>{text.length} / 4,000</Typography.Text>
          <Button
            type="primary"
            icon={<CommentOutlined />}
            onClick={handleSubmit}
            loading={loading}
            disabled={disabled || !canSubmit}
          >
            {submitLabel}
          </Button>
        </Space>
      </div>
    </Space>
  );
}

// Renders attachment thumbnails/links below a comment's text.
// Fetches via the authenticated api instance (Bearer token) then uses blob URLs.
export function CommentAttachments({ attachments }) {
  const [blobUrls, setBlobUrls] = useState({});

  useEffect(() => {
    if (!attachments?.length) return;
    const urls = {};
    let cancelled = false;
    Promise.all(
      attachments.map((a) =>
        api.get(`/api/attachments/${a.ID}`, { responseType: 'blob' })
          .then((res) => { urls[a.ID] = URL.createObjectURL(res.data); })
          .catch(() => {})
      )
    ).then(() => {
      if (!cancelled) setBlobUrls(urls);
    });
    return () => {
      cancelled = true;
      Object.values(urls).forEach(URL.revokeObjectURL);
    };
  }, [attachments]);

  if (!attachments?.length) return null;

  const openBlob = (id) => {
    if (blobUrls[id]) window.open(blobUrls[id], '_blank');
  };

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 }}>
      {attachments.map((a) =>
        a.ContentType?.startsWith('image/') ? (
          <img
            key={a.ID}
            src={blobUrls[a.ID] || ''}
            alt={a.FileName}
            onClick={() => openBlob(a.ID)}
            style={{
              maxHeight: 120, maxWidth: 200, objectFit: 'contain',
              borderRadius: 4, border: '1px solid var(--ant-color-border)',
              cursor: blobUrls[a.ID] ? 'pointer' : 'default',
              display: blobUrls[a.ID] ? 'block' : 'none',
            }}
          />
        ) : (
          <span
            key={a.ID}
            onClick={() => openBlob(a.ID)}
            style={{ fontSize: 12, cursor: blobUrls[a.ID] ? 'pointer' : 'default', color: 'var(--ant-color-primary)' }}
          >
            <PaperClipOutlined /> {a.FileName}
          </span>
        )
      )}
    </div>
  );
}
